
// @ts-ignore
// @ts-nocheck
import { useEffect, useState, type ComponentType } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    FacebookIcon,
    InstagramIcon,
    YoutubeIcon,
    SoundcloudIconSVG,
    SpotifyIcon,
    AppleMusicIconSVG,
    BandcampIconSVG,
    GlobeIcon,
    TikTokIcon,
    SnapchatIcon,
    DiscordIcon,
    TwitchIcon,
    XLogoIcon,
    LinkedInIcon,
    ThreadsIcon,
    NewgroundsIcon,
} from '../../icons';
import { ProfileInfoType } from '../../../pages/Profile';
import PlusIcon from '../../icons/PlusIcon';

interface SocialLinksProps {
    profileInfo: ProfileInfoType
    setIsEditing: (val: boolean) => void
    canEdit?: boolean
}

const slideClass = 'flex bg-transparent h-full justify-center items-center';

const SOCIAL_LINKS: Array<{
    field: keyof ProfileInfoType
    label: string
    Icon: ComponentType<{ width?: number; height?: number }>
    slideClassName?: string
}> = [
    { field: 'personalUrl', label: 'Personal website', Icon: GlobeIcon },
    { field: 'facebook', label: 'Facebook', Icon: FacebookIcon },
    { field: 'instagram', label: 'Instagram', Icon: InstagramIcon },
    { field: 'tiktok', label: 'TikTok', Icon: TikTokIcon },
    { field: 'youtube', label: 'YouTube', Icon: YoutubeIcon },
    { field: 'spotify', label: 'Spotify', Icon: SpotifyIcon },
    { field: 'soundcloud', label: 'SoundCloud', Icon: SoundcloudIconSVG },
    { field: 'applemusic', label: 'Apple Music', Icon: AppleMusicIconSVG, slideClassName: 'flex bg-transparent h-full' },
    { field: 'bandcamp', label: 'Bandcamp', Icon: BandcampIconSVG },
    { field: 'snapchat', label: 'Snapchat', Icon: SnapchatIcon },
    { field: 'discord', label: 'Discord', Icon: DiscordIcon },
    { field: 'twitch', label: 'Twitch', Icon: TwitchIcon },
    { field: 'x', label: 'X', Icon: XLogoIcon },
    { field: 'linkedin', label: 'LinkedIn', Icon: LinkedInIcon },
    { field: 'threads', label: 'Threads', Icon: ThreadsIcon },
    { field: 'newgrounds', label: 'Newgrounds', Icon: NewgroundsIcon },
];

function SocialLinks({ profileInfo, setIsEditing, canEdit }: SocialLinksProps) {
    const [swiper, setSwiper] = useState<typeof Swiper>();
    const [showRightArrow, setShowRightArrow] = useState(false);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [noSlides, setNoSlides] = useState(false);
    const fieldsToCheck = SOCIAL_LINKS.map((item) => item.field);

    const slideNext = () => {
        swiper?.slideNext();
    };

    const slidePrev = () => {
        swiper?.slidePrev();
    };

    const onSlideChange = () => {
        const activeIndex = swiper?.activeIndex;
        if (activeIndex > 0) {
            setShowLeftArrow(true);
        } else {
            setShowLeftArrow(false);
        }

        if (swiper?.isEnd) {
            setShowRightArrow(false);
        }
    };

    const onFromEdge = () => {
        if (!swiper?.isEnd) {
            setShowRightArrow(true);
        }
    };

    useEffect(() => {
        // Count the number of fields with values
        const countOfFieldsWithValues = fieldsToCheck.reduce((count, field) => {
            if (profileInfo && profileInfo[field]) {
                count++;
            }
            return count;
        }, 0);
        if (countOfFieldsWithValues > 4) {
            setShowRightArrow(true);
        }
        if (countOfFieldsWithValues <= 4) {
            setShowRightArrow(false);
        }
        if (countOfFieldsWithValues <= 0) {
            setNoSlides(true);
        } else {
            setNoSlides(false);
        }
    }, [profileInfo]);

    return !noSlides ? (
        <>
            <div className="flex cursor-pointer h-[37px] w-[24px]">
                {showLeftArrow && (
                    <div
                        onClick={slidePrev}
                        className="flex items-center justify-center w-full h-full"
                    >
                        <ChevronLeftIcon />
                    </div>
                )}
            </div>
            <Swiper
                slidesPerView={4}
                slidesPerGroup={4}
                onSlideChange={() => onSlideChange()}
                onFromEdge={() => onFromEdge()}
                onSwiper={(swiper) => setSwiper(swiper)}
                modules={[Pagination]}
                className="h-full w-[160px]"
            >
                {SOCIAL_LINKS.map(({ field, label, Icon, slideClassName }) => {
                    const href = profileInfo?.[field];
                    if (typeof href !== 'string' || !href) return null;
                    return (
                        <SwiperSlide
                            key={field}
                            className={slideClassName || slideClass}
                        >
                            <a
                                href={href}
                                target="_blank"
                                aria-label={label}
                                rel="ugc nofollow noreferrer"
                                className="flex w-full h-full justify-center items-center"
                            >
                                <Icon width={37} height={37} />
                            </a>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
            <div className="flex cursor-pointer h-[37px] w-[24px]">
                {showRightArrow && (
                    <div
                        onClick={slideNext}
                        className="flex items-center justify-center w-full h-full"
                    >
                        <ChevronRightIcon />
                    </div>
                )}
            </div>
        </>
    ) : (
        canEdit ?
            (
                <button
                    className="flex justify-center items-center h-[37px] w-[160px] bg-blue-200 rounded"
                    onClick={() => setIsEditing(true)}
                >
                    <PlusIcon />
                    <p className="text-xs ">Add social links</p>
                </button>
            ) :
            (
                <></>
            )
    );
}

export default SocialLinks;

